import { useState } from 'react';
import { api } from '../lib/api';
import { DataTable, ErrorBox, money, type Column, type Row } from '../components/DataTable';

/**
 * 재무추가 **대량 등록**(엑셀) — 레거시 `ResultDailyUpload` 이식.
 *
 * 실측상 주 입력 경로다(엑셀 41,361 · 카드 25,053 · 단건 25,839).
 *
 * 흐름은 레거시와 같다: **엑셀 → 전건 검증 → 하나라도 오류면 막음 → 등록 실행.**
 * 부분 등록을 허용하면 어디까지 들어갔는지 아무도 모른다.
 *
 * ⚠️ 매칭 키는 **편성번호**(car_alloc_id)다. 이름·차량번호로 찾지 않는다 —
 *    "현재 편성"이 유일하지 않아서(차량 1대당 최대 205건) 이름 매칭은 반드시 어긋난다.
 *    편성번호는 정산현황·편성 화면에서 확인할 수 있다.
 *
 * ⚠️ xlsx 파싱은 **브라우저에서** 한다. 파일이 사용자 PC 에 있고, 서버로 올릴 이유가 없다
 *    (개인정보가 섞인 파일을 굳이 전송·보관하지 않는다). 파서는 이 화면에서만 동적 로드한다.
 */

const COLUMNS = ['편성번호', '계정', '지급공제구분', '금액', '비고', '실행일자'] as const;

interface ParsedRow {
  carAllocId: string; accountName: string; io: string;
  amount: string; comment: string; exDate: string;
}

export default function ResultDailyUpload({ driveMon, onDone }: { driveMon: string; onDone: () => void }) {
  const [rows, setRows] = useState<ParsedRow[]>([]);
  const [fileName, setFileName] = useState('');
  const [preview, setPreview] = useState<Awaited<ReturnType<typeof api.previewResultDailyUpload>> | null>(null);
  const [busy, setBusy] = useState<'template' | 'parse' | 'check' | 'commit' | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState<{ created: number; plus: string; minus: string } | null>(null);

  /**
   * 템플릿 내려받기 — **레거시에 없던 기능**. 레거시 "내보내기"는 그리드를 엑셀로 뽑는 것이라
   * 편성번호를 사용자가 다른 화면에서 찾아와야 했다.
   *
   * 시트 3장: `입력`(헤더 + 예시 1행) · `편성목록`(편성번호 조회용) · `계정목록`(계정오류 예방).
   * ⚠️ 입력 시트에 편성 3,847건을 미리 깔지 않는다 — 안 쓰는 행을 지우는 게 더 큰 일이 된다.
   */
  async function downloadTemplate() {
    setBusy('template'); setError(null);
    try {
      const [XLSX, t] = await Promise.all([import('xlsx'), api.getResultDailyTemplate()]);
      const wb = XLSX.utils.book_new();

      // 입력 시트 — 헤더 + 예시 1행(첫 편성으로 채워 형식을 보여준다)
      const sample = t.allocs[0];
      const input = [
        [...COLUMNS],
        sample
          ? [sample.car_alloc_id, t.accounts[0]?.account_name ?? '', t.accounts[0]?.is_plus ? '지급' : '공제', 0, '(예시 행 — 지우고 쓰세요)', '']
          : [...COLUMNS.map(() => '')],
      ];
      XLSX.utils.book_append_sheet(wb, XLSX.utils.aoa_to_sheet(input), '입력');

      XLSX.utils.book_append_sheet(wb, XLSX.utils.aoa_to_sheet([
        ['편성번호', '소속', '차주', '기사', '차량번호'],
        ...t.allocs.map((a) => [a.car_alloc_id, a.grp_name ?? '', a.owner_name ?? '', a.driver_name ?? '', a.car_no ?? '']),
      ]), '편성목록');

      XLSX.utils.book_append_sheet(wb, XLSX.utils.aoa_to_sheet([
        ['계정명', '지급공제구분'],
        ...t.accounts.map((a) => [a.account_name, a.is_plus ? '지급' : '공제']),
      ]), '계정목록');

      XLSX.writeFile(wb, `재무추가_${driveMon}_템플릿.xlsx`);
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally { setBusy(null); }
  }

  async function pick(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (!file) return;
    setBusy('parse'); setError(null); setPreview(null); setDone(null);
    try {
      // 이 화면에서만 파서를 로드한다 — 다른 화면 번들을 키우지 않는다.
      const XLSX = await import('xlsx');
      const buf = await file.arrayBuffer();
      const wb = XLSX.read(buf, { type: 'array' });
      const sheet = wb.Sheets[wb.SheetNames[0]!];
      if (!sheet) throw new Error('시트를 찾을 수 없습니다.');
      // 값은 전부 문자열로 받는다 — 숫자로 받으면 금액 소수·앞자리 0이 흔들린다.
      const raw = XLSX.utils.sheet_to_json<Record<string, unknown>>(sheet, { raw: false, defval: '' });
      if (raw.length === 0) throw new Error('행이 없습니다.');

      const head = Object.keys(raw[0]!);
      const missing = COLUMNS.filter((c) => !head.includes(c));
      if (missing.length) {
        throw new Error(`엑셀에 다음 열이 없습니다: ${missing.join(', ')} (필요한 열: ${COLUMNS.join(' · ')})`);
      }

      setRows(raw.map((r) => ({
        carAllocId: String(r['편성번호'] ?? '').trim(),
        accountName: String(r['계정'] ?? '').trim(),
        io: String(r['지급공제구분'] ?? '').trim(),
        amount: String(r['금액'] ?? '').trim(),
        comment: String(r['비고'] ?? '').trim(),
        exDate: String(r['실행일자'] ?? '').trim(),
      })));
      setFileName(file.name);
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
      setRows([]); setFileName('');
    } finally {
      setBusy(null);
      e.target.value = ''; // 같은 파일을 다시 고를 수 있게
    }
  }

  async function check() {
    setBusy('check'); setError(null); setDone(null);
    try {
      setPreview(await api.previewResultDailyUpload({
        driveMon,
        rows: rows.map((r) => ({
          carAllocId: r.carAllocId, accountName: r.accountName, io: r.io,
          amount: r.amount, comment: r.comment, exDate: r.exDate,
        })),
      }));
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
      setPreview(null);
    } finally { setBusy(null); }
  }

  async function commit() {
    setBusy('commit'); setError(null);
    try {
      const r = await api.commitResultDailyUpload({
        driveMon,
        rows: rows.map((x) => ({
          carAllocId: x.carAllocId, accountName: x.accountName, io: x.io,
          amount: x.amount, comment: x.comment, exDate: x.exDate,
        })),
      });
      setDone({ created: r.created, plus: r.plus, minus: r.minus });
      setRows([]); setPreview(null); setFileName('');
      onDone();
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally { setBusy(null); }
  }

  const errorCols: Column[] = [
    { key: 'row', label: '행', align: 'right' },
    { key: 'errors', label: '오류', render: (r) => <span className="text-red-700">{(r.errors as string[]).join(' · ')}</span> },
  ];

  const canCommit = preview !== null && preview.errorCount === 0 && !preview.isFinished && rows.length > 0;

  return (
    <div className="card space-y-3 p-4">
      <div className="flex flex-wrap items-baseline gap-3">
        <h2 className="text-sm font-bold">{driveMon} 대량 등록</h2>
        <span className="text-xs text-ink-faint">
          엑셀 열: {COLUMNS.join(' · ')} — <b>편성번호</b>로 기사·차주·차량을 찾습니다
        </span>
      </div>

      {error && <ErrorBox message={error} />}

      <div className="flex flex-wrap items-center gap-3">
        <button className="btn-ghost text-xs" onClick={downloadTemplate} disabled={busy !== null}>
          {busy === 'template' ? '만드는 중…' : '템플릿 내려받기'}
        </button>
        <label className="btn-ghost cursor-pointer text-xs">
          {busy === 'parse' ? '읽는 중…' : '엑셀 선택'}
          <input type="file" accept=".xlsx,.xls,.csv" className="hidden" onChange={pick} disabled={busy !== null} />
        </label>
        {fileName && (
          <span className="text-sm">
            {fileName} — <b>{rows.length.toLocaleString('ko-KR')}행</b>
          </span>
        )}
        {rows.length > 0 && (
          <button className="btn-ghost text-xs" onClick={check} disabled={busy !== null}>
            {busy === 'check' ? '검증 중…' : '검증'}
          </button>
        )}
      </div>

      {preview && (
        <>
          <div className="flex flex-wrap items-center gap-4 rounded-lg border border-line bg-surface px-3 py-2 text-sm">
            <span>총 <b>{preview.total.toLocaleString('ko-KR')}</b>행</span>
            <span className="text-emerald-700">정상 {preview.okCount.toLocaleString('ko-KR')}</span>
            <span className={preview.errorCount > 0 ? 'text-red-700 font-bold' : 'text-ink-faint'}>
              오류 {preview.errorCount.toLocaleString('ko-KR')}
            </span>
            <span>지급 <b>{money(preview.sumPlus)}</b></span>
            <span>공제 <b>{money(preview.sumMinus)}</b></span>
          </div>

          {preview.isFinished && (
            <ErrorBox message="관리자 정산이 완료된 달이라 대량 등록할 수 없습니다. 마감을 먼저 푸세요." />
          )}

          {preview.errorCount > 0 && (
            <>
              <p className="text-sm text-red-700">
                검증되지 않은 항목이 있습니다. <b>전부 고친 뒤</b> 다시 올려야 등록됩니다 —
                일부만 등록하면 어디까지 들어갔는지 알 수 없습니다.
              </p>
              <DataTable columns={errorCols} rows={preview.errors as unknown as Row[]} loading={false}
                         tableId="upload.excel.errors" />
            </>
          )}

          {canCommit && (
            <button className="btn" onClick={commit} disabled={busy !== null}>
              {busy === 'commit' ? '등록 중…' : `${preview.total.toLocaleString('ko-KR')}행 등록 실행`}
            </button>
          )}
        </>
      )}

      {done && (
        <div className="rounded-lg border border-emerald-200 bg-emerald-50 p-3 text-sm text-emerald-900">
          <b>{done.created.toLocaleString('ko-KR')}행을 등록했습니다.</b> 지급 {money(done.plus)} · 공제 {money(done.minus)}
        </div>
      )}
    </div>
  );
}
